import { styled, css } from '@vebox/etui-system';
import { memo, useState, createElement, Fragment } from 'react';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import '../ClickOutside/ClickOutside.js';
import ExtendedTooltip from '../Tooltip/ExtendedTooltip.js';
import { format } from 'date-fns';

const _excluded = ["isSelecting", "isEditing", "disabled", "transparentBg", "theme"],
  _excluded2 = ["isStart", "isEnd", "isHidden", "disabled", "theme"];
const parseEventHeight = (height = '', top = '') => {
  const parsedHeight = parsePercent(height);
  const parsedTop = parsePercent(top);
  return parsedTop < 0 ? `${parsedHeight + parsedTop}%` : height;
};
const parsePercent = str => {
  const regex = /[%]{1}/gi;
  return +str.replace(regex, '');
};
const EventDiv = styled(Div).attrs(_ref => {
  let {
      isSelecting,
      isEditing,
      disabled,
      transparentBg,
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  let opacity = 1;
  if (isSelecting) {
    opacity = 0.5;
  }
  if (isEditing) {
    opacity = 0;
  }
  if (disabled) {
    opacity = 0.3;
  }
  return _objectSpread2(_objectSpread2({
    display: 'flex',
    position: 'absolute',
    maxHeight: '100%',
    flexFlow: 'column wrap',
    alignItems: 'flex-start',
    opacity,
    bg: transparentBg ? 'transparent' : theme.palette.primary.main,
    borderRadius: `${theme.shape.borderRadius}px`,
    cursor: disabled ? 'default' : 'pointer'
  }, props), {}, {
    width: props.width ? props.width : '42px',
    left: props.left ? props.left : '-1px',
    top: parsePercent(props.top) <= 0 ? 0 : props.top,
    height: parseEventHeight(props.height, props.top)
  });
})`
  border: none;
  margin: 0;
  text-align: left;
`;
const EventPeriodDiv = styled(Div)`
  opacity: ${props => props.isEditing ? 0 : 1};
  position: absolute;
  left: -1px;
  width: 42px;
  background-color: ${({
  tense,
  theme
}) => tense === 'past' ? theme.palette.action.disabled : theme.palette.primary.main};

  ${({
  height,
  top
}) => {
  return css`
      height: ${height}%;
      top: ${top}%;
    `;
}};
`;
const EventTimeDiv = styled(Div).attrs(_ref2 => {
  let {
      isStart,
      isEnd,
      isHidden,
      disabled,
      theme
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded2);
  return _objectSpread2({
    position: 'absolute',
    top: isStart ? `calc(-${theme.spacing.lg}px)` : 'auto',
    bottom: isEnd ? `calc(-${theme.spacing.lg}px)` : 'auto',
    right: 0,
    height: `${theme.spacing.lg}px`,
    width: `100%`,
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightExtraBold,
    color: theme.palette.text.primary,
    opacity: isHidden ? '0' : '1',
    cursor: disabled ? 'default' : 'row-resize',
    textAlign: 'right'
  }, props);
})``;

var OFFSETS = /*#__PURE__*/function (OFFSETS) {
  OFFSETS[OFFSETS["LIMIT"] = 200] = "LIMIT";
  OFFSETS[OFFSETS["LARGE"] = 175] = "LARGE";
  OFFSETS[OFFSETS["MEDIUM"] = 125] = "MEDIUM";
  OFFSETS[OFFSETS["SMALL"] = 25] = "SMALL";
  return OFFSETS;
}(OFFSETS || {});
const Event = /*#__PURE__*/memo(function Event(props) {
  const {
    event,
    timeFormat,
    style,
    onClick,
    currentTimePointDimensions,
    isEditing,
    isTodayDisabled,
    isSelecting,
    styles = {},
    isTimeStartLabelHidden = false,
    isTimeEndLabelHidden = false,
    onMouseHover,
    onMouseDown,
    onTouchStart
  } = props;
  const {
    startDate,
    endDate
  } = event;
  const {
    height,
    top
  } = style;
  const [eventParams, setEventParams] = useState({
    x: null,
    y: null,
    selected: false
  });
  const handleOnClick = () => {
    if (onClick) {
      onClick(event);
    }
  };
  const handleMouseDown = () => {
    if (onMouseDown) {
      onMouseDown(event);
    }
  };
  const handleMouseOver = () => {
    if (onMouseHover) {
      onMouseHover();
    }
  };
  const handleTouchStart = () => {
    if (onTouchStart) {
      onTouchStart(event);
    }
  };
  const checkTooltipVisibility = (componentBound, eventBound) => componentBound - eventBound < OFFSETS.LIMIT;
  const renderTimeLabel = time => {
    return format(time, timeFormat).replace(/(?!-)[^0-9:]/g, '');
  };
  const handleMouseHover = e => {
    var _document$querySelect;
    const bounds = e.target.getBoundingClientRect();
    const {
      clientY,
      clientX
    } = e;
    const calendar = (_document$querySelect = document.querySelector('#calendar-container')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.getBoundingClientRect();
    const isBottomReached = checkTooltipVisibility(calendar.bottom, bounds.bottom);
    const isRightBorderReached = checkTooltipVisibility(calendar.right, bounds.right);
    const isRightBorderReachedInCalendar = checkTooltipVisibility(window.innerWidth, bounds.right);
    const x = clientX - bounds.left;
    const y = clientY - bounds.top;
    setEventParams({
      x: isRightBorderReached || isRightBorderReachedInCalendar ? x - OFFSETS.LARGE : x + OFFSETS.SMALL,
      y: isBottomReached ? y - OFFSETS.MEDIUM : y + OFFSETS.SMALL,
      selected: true
    });
    onMouseHover();
  };
  const handleMouseLeave = () => {
    setEventParams({
      x: null,
      y: null,
      selected: false
    });
  };
  const currentTimeStamp = new Date().getTime();
  const isPastTimeTillNowVisible = !isTodayDisabled && currentTimePointDimensions && event.startDate.getTime() < currentTimeStamp && event.endDate.getTime() > currentTimeStamp;
  if (event.onRender || props.onNewEventRender) {
    const getContainerProps = () => ({
      onMouseOver: () => handleMouseOver(),
      onMouseDown: () => handleMouseDown(),
      onClick: () => handleOnClick()
    });
    const onRenderProps = _objectSpread2(_objectSpread2({}, props), {}, {
      isPastTimeTillNowVisible,
      renderTimeLabel,
      getContainerProps
    });
    if (isSelecting) {
      return props.onNewEventRender(onRenderProps);
    }
    return event.onRender(onRenderProps);
  }
  const shouldShowTooltip = event.tooltipMessage && eventParams.selected;
  return /*#__PURE__*/createElement(Fragment, null, isPastTimeTillNowVisible && /*#__PURE__*/createElement(Div, {
    cursor: "pointer",
    onFocus: onMouseHover,
    onMouseOver: onMouseHover
  }, /*#__PURE__*/createElement(EventPeriodDiv, {
    isEditing: isEditing,
    height: currentTimePointDimensions.height,
    top: currentTimePointDimensions.top,
    tense: "past",
    onClick: handleOnClick,
    "data-todayevent": true
  }), /*#__PURE__*/createElement(EventPeriodDiv, {
    isEditing: isEditing,
    height: height - currentTimePointDimensions.height,
    top: currentTimePointDimensions.height + currentTimePointDimensions.top,
    tense: "present",
    onClick: handleOnClick,
    "data-todayevent": true
  })), /*#__PURE__*/createElement(EventDiv, _extends({
    className: "event",
    onFocus: onMouseHover,
    onMouseOver: handleMouseHover,
    onMouseLeave: handleMouseLeave,
    id: "event",
    top: `${top}%`,
    isSelecting: isSelecting,
    isEditing: isEditing
    // @ts-ignore
    ,
    height: `${height}%`,
    testId: `${startDate}-${endDate}`,
    transparentBg: isPastTimeTillNowVisible,
    onMouseDown: handleMouseDown,
    onTouchStart: handleTouchStart,
    onClick: handleOnClick
  }, styles.root), /*#__PURE__*/createElement(ExtendedTooltip, {
    title: event === null || event === void 0 ? void 0 : event.tooltipMessage,
    content: event === null || event === void 0 ? void 0 : event.tooltipContent,
    displayTooltip: shouldShowTooltip,
    defaultPlacement: false,
    styles: {
      wrapper: {
        top: `${eventParams.y}px`,
        left: `${eventParams.x}px`
      }
    }
  }), event.hideEventMarkers ? null : /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(EventTimeDiv, _extends({
    disabled: event.disabled,
    className: "isStart",
    isStart: true,
    isHidden: isTimeStartLabelHidden
  }, styles.label), renderTimeLabel(startDate)), /*#__PURE__*/createElement(EventTimeDiv, _extends({
    disabled: event.disabled,
    className: "isEnd",
    isEnd: true,
    isHidden: isTimeEndLabelHidden
  }, styles.label), renderTimeLabel(endDate)))));
});

export default Event;
